import React, { FC } from 'react';
import { BasketCashPaymentSelectionProps } from '@components/v2/BasketCashPaymentSelection';
import useTranslations from '@hooks/useTranslations';
import { BUDGET_TYPE_CASH } from '../../../services';
import { useMoneyFormatter } from '@hooks/useMoneyFormatter';
import { usePrices } from '@hooks/usePriceCalculator';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import routes from '../../../constants/routes';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST } from '../../../context/featureSwitchApp';
import { useRouter } from 'next/router';
import PriceSuffix from '@components/PriceSuffix';

const BasketCashPaymentSelectionTemplate: FC<BasketCashPaymentSelectionProps> = ({
    isSelected,
    onClick,
    className,
}) => {
    const { t } = useTranslations();

    const router = useRouter();

    const { formatMoney } = useMoneyFormatter();

    const { carDetails, currentDeal, promoCodeInDeal } = useCarDetailsDuck();

    const showPaymentDeposit = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_SHOW_PAYMENT_DEPOSIT);
    const isDealerFeesFsEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_DEALER_FEES_ENABLED);
    const isDealerFeesEnabled = !router.pathname.includes(routes.DEALER) && isDealerFeesFsEnabled;

    const { hasSelectedPaymentJourney = false } = currentDeal?.extraFields ?? {};
    const { cash } = usePrices(carDetails ?? null, false, null, true, !isDealerFeesEnabled);
    const {
        totalPrice: totalCashPrice,
        totalPriceWithoutPaymentJourney: { cash: totalCashPriceWithoutSelectedPaymentJourney },
    } = cash;
    const fee =
        promoCodeInDeal && JSON.parse(promoCodeInDeal)?.depositAmount
            ? JSON.parse(promoCodeInDeal)?.depositAmount
            : currentDeal?.extraFields?.reservationFee;

    return (
        <div className={className}>
            <button
                className={`section ${isSelected ? 'selected' : ''}`}
                onClick={() => onClick(BUDGET_TYPE_CASH)}
                data-testid="TESTING_SELECTION_CASH"
            >
                <div className="inner">
                    <span className="title">
                        <span className={`icon ${isSelected ? 'selected' : ''}`}>
                            <span className={isSelected ? 'selected' : ''} />
                        </span>{' '}
                        {t('basket.payment.cash.title')}
                    </span>
                    <span className="description">{t('basket.payment.cash.description')}</span>
                    <div className="summary">
                        <span className="totalPrice">
                            {formatMoney(
                                !hasSelectedPaymentJourney
                                    ? totalCashPriceWithoutSelectedPaymentJourney +
                                          (carDetails?.importInputs?.accessoriesPrice || 0)
                                    : totalCashPrice + (carDetails?.importInputs?.accessoriesPrice || 0),
                                false
                            )}
                        </span>
                        {isSelected && (
                            <>
                                <div className="otherPrices">
                                    {showPaymentDeposit && fee > 0 && (
                                        <div className="otherPrice">
                                            <span>{t('basket.payment.selectedCash.deposit')}</span>
                                            <span>
                                                <strong>{formatMoney(fee, true)}</strong>
                                            </span>
                                        </div>
                                    )}
                                    {showPaymentDeposit && fee > 0 && (
                                        <div className="otherPrice">
                                            <span>{t('basket.payment.selectedCash.remainingAmount')}</span>
                                            <span>
                                                <strong>{formatMoney(totalCashPrice - fee)}</strong>
                                            </span>
                                        </div>
                                    )}
                                    <div className="otherPrice">
                                        <span>
                                            <strong>{t('offerDetail.totalPrice.title')}</strong>
                                        </span>
                                        <span>
                                            <strong>
                                                {formatMoney(totalCashPrice)}{' '}
                                                <PriceSuffix
                                                    checkPrioritizedTranslation
                                                    showOnlyPrioritizedTranslation
                                                />
                                            </strong>
                                        </span>
                                    </div>
                                </div>
                                <p
                                    className="description"
                                    dangerouslySetInnerHTML={{ __html: t('basket.payment.selectedCash.description1') }}
                                />
                                <p
                                    className="description"
                                    dangerouslySetInnerHTML={{ __html: t('basket.payment.selectedCash.description2') }}
                                />
                            </>
                        )}
                    </div>
                </div>
            </button>
        </div>
    );
};

export default BasketCashPaymentSelectionTemplate;
